/**
 * Shared holiday-aware working-hours utility (260929-bq9 / MKS-179).
 *
 * Straight extraction of the already-correct algorithm from
 * `TimeEntryPage.tsx`'s `maxWorkingHours` useMemo — no behavior change to
 * that reference logic, only relocation so `CurrentMonthTimesheetCard` and
 * `RecentTimesheetsCard` can share the exact same calculation instead of
 * each maintaining their own (previously divergent) math.
 */

export const DAILY_TARGET_HOURS = 8;

/**
 * Count working days (Mon-Fri, excluding holidays) in the given month.
 *
 * `monthIndex` is zero-based per JS `Date` convention (matches the removed
 * `businessDaysInMonth`'s existing signature so both call sites migrate
 * with a like-for-like argument order).
 *
 * A holiday that falls on a weekend does not double-subtract: a day is only
 * ever excluded once, via a single `!isWeekend && !isHoliday` check.
 */
export function countWorkingDaysInMonth(
  year: number,
  monthIndex: number,
  holidayDates: Set<string>,
): number {
  let workingDays = 0;
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, monthIndex, day);
    const dateStr = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isHoliday = holidayDates.has(dateStr);
    if (!isWeekend && !isHoliday) {
      workingDays++;
    }
  }
  return workingDays;
}

/** Monthly hours target: working days in the month * `DAILY_TARGET_HOURS`. */
export function getMonthlyTargetHours(
  year: number,
  monthIndex: number,
  holidayDates: Set<string>,
): number {
  return countWorkingDaysInMonth(year, monthIndex, holidayDates) * DAILY_TARGET_HOURS;
}
